// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TemplateGroup } from '@interfaces/document.interfaces';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { catchError, takeWhile } from 'rxjs';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { ActiveInternalDocumentTypePipe } from 'src/app/pipes/active-internal-document-type.pipe';
import { CustomDealTypesHelpPanelComponent } from 'src/app/shared/help-panels/custom-deal-types-help/custom-deal-types-help-panel.component';
import { showFormErrors } from 'src/app/utils/form-utils';

@Component({
    selector: 'custom-deal-group',
    templateUrl: './custom-deal-group.component.html',
    styleUrls: ['./custom-deal-group.component.scss'],
})
export class CustomDealGroupsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;
    @ViewChild('helpPanel') helpPanel: TemplateRef<CustomDealTypesHelpPanelComponent>;
    public createGroupForm: FormGroup;
    private element = null;
    public groups: TemplateGroup[] = [];
    private isComponentAlive = true;
    public isLoading = false;
    public noSelectedTemplatesError = false;
    public selectAllTemplatesCheckbox = false;
    public selectedGroup: TemplateGroup;
    private selectedTemplates: any[] = [];
    public templates: any[] = [];
    public createClicked = false;
    public isDirty = false;

    constructor(
        private dialog: MatDialog,
        private documentService: DocumentsService,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private errorService: ErrorService,
        private readonly navService: NavService,
        private userService: UserService,
        private readonly activeDocumentPipe: ActiveInternalDocumentTypePipe
    ) {}

    @HostListener('window:beforeunload')
    canDeactivate(): boolean {
        return !this.isDirty;
    }

    ngAfterViewInit(): void {
        this.navService.setHelpPanel(this.helpPanel);
    }

    ngOnInit(): void {
        this.templates = this.activeDocumentPipe.transform(this.route.snapshot.data.templates);

        this.groups = this.route.snapshot.data.groups;

        this.buildForm();
        this.element = document.querySelector('#scrollTo');
        this.navService.setTitle('Custom Deal Types');

        if (this.groups?.length > 0) {
            this.resetGroup(true);
            this.isDirty = false;
        }
    }

    ngOnDestroy(): void {
        this.navService.setHelpPanel(null);
        this.isComponentAlive = false;
    }

    get templateArray(): FormArray {
        return this.createGroupForm.get('templates') as FormArray;
    }

    dirtyFormCheck(callback: () => void) {
        if (!this.isDirty) {
            callback();
        } else {
            this.dialog
                .open(ConfirmModalComponent, {
                    width: '700px',
                    data: {
                        title: 'You Have Unsaved Changes',
                        message:
                            'You have made modifications to this Custom Deal Type that have not been saved. If you do not want to keep them, click Confirm. Otherwise, click Cancel and continue modifying and / or save the deal type.',
                        btnText: 'Confirm',
                    },
                })
                .afterClosed()
                .subscribe((confirmed: boolean) => {
                    if (confirmed) {
                        this.isDirty = false;
                        callback();
                    }
                });
        }
    }

    private buildForm(): void {
        this.createGroupForm = this.fb.group({
            name: [null, Validators.compose([Validators.maxLength(250), Validators.required])],
            templates: this.fb.array([]),
        });

        this.createTemplateFormArray();
        this.onChanges();
    }

    public checked(template: any): void {
        this.isDirty = true;
        template.checked = !template.checked;
    }

    private createTemplateFormArray(): void {
        this.templates.forEach((x, i) => {
            const fg = this.fb.group({});
            fg.addControl(
                this.templates[i].displayName,
                new FormControl({
                    value: x.systemDocument ? true : false,
                    disabled: x.systemDocument ? true : false, //here is the disable
                })
            );

            if (x.systemDocument) {
                this.templates[i].checked = true;
            }

            this.templateArray.push(fg);
        });
    }

    public deleteGroup(): void {
        const groupName = this.selectedGroup?.groupName;
        this.dialog
            .open(ConfirmModalComponent, {
                width: '700px',
                closeOnNavigation: false,
                data: {
                    title: 'Delete Custom Deal Type',
                    richMessage: `Are you sure you want to delete <strong>${groupName}</strong>?<br /><br />Note: Deleting a Custom Deal Type does not affect any existing Deal Jackets; it only disables it from being used in future Deal Jackets.`,
                    btnText: 'Delete',
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result === true) {
                    this.isLoading = true;
                    this.documentService
                        .deleteTemplateGroup$(this.selectedGroup.templateGroupId)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((error) => {
                                this.isLoading = false;
                                this.errorService.showGenericError();
                                throw error;
                            })
                        )
                        .subscribe(() => {
                            this.loadGroups();
                            this.resetGroup();
                            this.dialog
                                .open(SuccessModalComponent, {
                                    width: '700px',
                                    data: {
                                        title: 'Custom Deal Type Deleted',
                                        richContent: `<strong>${groupName}</strong> has been deleted successfully.`,
                                    },
                                })
                                .afterClosed()
                                .pipe(takeWhile(() => this.isComponentAlive))
                                .subscribe(() => {
                                    this.element.scrollIntoView();
                                    this.isLoading = false;
                                    this.isDirty = false;
                                });
                        });
                }
            });
    }

    public editGroup(group: TemplateGroup): void {
        this.dirtyFormCheck(() => {
            this.selectAllTemplatesCheckbox = false;
            this.createClicked = false;
            this.selectedGroup = group;
            this.createGroupForm.controls.name.patchValue(group.groupName);
            this.templates.forEach((t) => (t.checked = false));
            this.setSelectedTemplates();
            this.isDirty = false;
        });
    }

    public loadGroups(): void {
        this.documentService
            .getTemplateGroupNames$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((response) => {
                this.groups = response;
            });
    }

    private onChanges(): void {
        this.createGroupForm.valueChanges.pipe(takeWhile(() => this.isComponentAlive)).subscribe((value) => {
            Object.keys(value).forEach((key) => {
                if (key === 'templates') {
                    this.noSelectedTemplatesError = false;
                }
            });
        });
    }

    public createNew(): void {
        this.dirtyFormCheck(() => {
            this.resetGroup();
            this.createClicked = true;
            this.isDirty = true;
        });
    }

    public resetGroup(init?: boolean): void {
        this.dirtyFormCheck(() => {
            this.selectAllTemplatesCheckbox = false;
            this.selectedGroup = null;
            this.createGroupForm.reset();
            !init && this.formGroupDirective.resetForm();
            this.selectedTemplates = [];
            this.templates.forEach((template) => (template.checked = false));
            this.buildForm();
            this.isDirty = false;
        });
    }

    public saveGroup(): void {
        const templates = this.validateForm();

        if (templates) {
            const formData: FormData = new FormData();
            const groupName = this.createGroupForm.get('name').value;
            const dealerId = this.userService.getDealerId();
            const groupId = this.selectedGroup ? this.selectedGroup.templateGroupId : null;

            formData.append('groupName', groupName);
            formData.append('templateGroupId', groupId?.toString());
            formData.append('dealerId', dealerId.toString());
            formData.append('templateDocumentIds', templates);

            this.isLoading = true;

            this.documentService
                .createOrUpdateTemplateGroup$(groupName, dealerId, groupId, templates)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.isLoading = false;
                        this.errorService.showGenericError();
                        throw error;
                    })
                )
                .subscribe((groupOfTemplates) => {
                    // When we switch to C# api groupOfTemplates will not be an array, it will be the actual group
                    // So we can remove this.createGroup() method
                    this.selectedGroup = this.createGroup(groupOfTemplates);
                    this.isLoading = false;
                    this.dialog
                        .open(SuccessModalComponent, {
                            width: '700px',
                            closeOnNavigation: true,
                            data: {
                                title: 'Custom Deal Type Saved Successfully!',
                                richContent: `<strong>${groupName}</strong> has been saved and can be used to create Deal Jackets. `,
                                btnText: 'Okay',
                            },
                        })
                        .afterClosed()
                        .pipe(takeWhile(() => this.isComponentAlive))
                        .subscribe(() => {
                            this.loadGroups();
                            this.isDirty = false;
                            this.resetGroup();
                        });
                });
        }
        this.element.scrollIntoView();
    }

    // Remove this when C# api is used
    public createGroup(groupOfTemplates): TemplateGroup {
        this.selectAllTemplatesCheckbox = false;
        const group = {
            groupName: groupOfTemplates[0].groupName,
            templateGroupId: groupOfTemplates[0].templateGroupId,
            templates: groupOfTemplates.map((template) => template.templateDocumentId),
        };

        return group;
    }

    public selectAll(): void {
        this.isDirty = true;
        this.selectAllTemplatesCheckbox = !this.selectAllTemplatesCheckbox;

        this.toggleAllCheckboxes();
    }

    private setSelectedTemplates() {
        //fetch the actual tempalate documents
        this.documentService
            .getTemplateGroup$(this.selectedGroup.templateGroupId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((response) => {
                this.selectedGroup = response[0];
                this.selectedGroup.templates.forEach((groupTemplate) => {
                    const template = this.templates.find((t) => t.documentId === groupTemplate);
                    if (template) {
                        template.checked = true;
                    }
                });

                //enable or diable checkboxes if document is in template
                this.templates.forEach((template, index) => {
                    if (template.systemDocument && !this.selectedGroup.templates.includes(template.documentId)) {
                        (this.templateArray.controls[index] as FormGroup).controls[template.displayName].enable();
                    } else if (template.systemDocument) {
                        (this.templateArray.controls[index] as FormGroup).controls[template.displayName].disable();
                    }
                });
            });
    }

    private toggleAllCheckboxes(): void {
        this.templates.forEach((template) => {
            if (template.systemDocument) {
                template.checked = true;
                return;
            }
            template.checked = this.selectAllTemplatesCheckbox;
        });

        if (this.selectAllTemplatesCheckbox) {
            this.noSelectedTemplatesError = false;
        }
    }

    private validateForm(): any {
        // show error if no group name & scroll so it's in view
        if (this.createGroupForm.status === 'INVALID') {
            showFormErrors(this.createGroupForm.controls);
            this.element.scrollIntoView();
            return false;
        }

        this.selectedTemplates = this.templates.filter((template) => template.checked);

        // show error if no templates selected
        if (this.selectedTemplates.length <= 0) {
            this.noSelectedTemplatesError = true;
            return false;
        }

        // return array of doc ids
        return this.selectedTemplates.map((template) => template.documentId);
    }
}
